/**
 * @description LYN-186 Job pipeline read client for TowTech Scope 1 BI.
 *
 * @dependencies apiClient, sendableCompanyId
 *
 * @notes Session company is X-Company-Id from apiClient. All fans out with a
 *   per-request header — query companyId is never sent. Read-only: aggregates,
 *   in-flight, windowed jobs. No second writer.
 */

import apiClient from './apiClient';
import { isSendableCompanyId } from '../../utils/sendableCompanyId';

/**
 * @param {object} [params]
 * @param {unknown} [companyId]
 * @returns {object}
 */
function readConfig(params, companyId) {
  const config = {};
  if (params && Object.keys(params).length > 0) config.params = params;
  if (isSendableCompanyId(companyId)) {
    config.headers = { 'X-Company-Id': companyId };
  }
  return Object.keys(config).length > 0 ? config : undefined;
}

/**
 * @param {object} params
 * @param {string} [params.grain]
 * @param {string} [params.from]
 * @param {string} [params.to]
 * @param {number} [params.weekday]
 * @param {number} [params.hour]
 * @param {string} [params.profile]
 * @param {string} [params.asOf]
 * @param {string} [params.groupBy]
 * @param {string} [params.companyId] header only — never a query param
 * @returns {Promise<object>}
 */
export function getAggregates(params = {}) {
  const { companyId, ...query } = params;
  return apiClient.get('/jobs/aggregates', readConfig(query, companyId)).then((r) => r.data);
}

/**
 * @param {{ companyId?: string }} [params]
 * @returns {Promise<object>}
 */
export function getInFlight(params = {}) {
  const config = readConfig(undefined, params.companyId);
  return (config ? apiClient.get('/jobs/in-flight', config) : apiClient.get('/jobs/in-flight'))
    .then((r) => r.data);
}

/**
 * @param {object} params
 * @param {string} params.from
 * @param {string} params.to
 * @param {string} [params.stamp]
 * @param {string} [params.companyId]
 * @returns {Promise<object>}
 */
export function listJobs(params = {}) {
  const { companyId, ...query } = params;
  return apiClient.get('/jobs', readConfig(query, companyId)).then((r) => r.data);
}

const jobsService = { getAggregates, getInFlight, listJobs };

export default jobsService;
